// ============================================
// Zod Validation Schemas
// ============================================
import { z } from 'zod';
import { LEAD_STATUS, LEAD_SOURCE, ACCOUNT_TYPE, INDUSTRY, OPPORTUNITY_STAGE, USER_ROLE, TENANT_USER_ROLE, } from '../types/enums.js';
// ============================================
// Common Schemas
// ============================================
// ID schema accepts:
// - Standard UUIDs: 00000000-0000-0000-0000-000000000001
// - Prefixed IDs: l0000000-0000-0000-0000-000000000001 (used in seed data for leads, accounts, etc.)
export const idSchema = z
    .string()
    .regex(/^[a-z0-9]{1,8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i, 'Invalid ID format - must be a valid UUID or prefixed UUID');
export const paginationSchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const sortSchema = z.object({
    sortBy: z.string().optional(),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export const listParamsSchema = paginationSchema.merge(sortSchema).extend({
    search: z.string().optional(),
});
// ============================================
// User Schemas
// ============================================
export const createUserSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    name: z.string().min(1, 'Name is required').max(100),
    role: z
        .enum([USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.MANAGER, USER_ROLE.USER])
        .default(USER_ROLE.USER),
});
export const updateUserSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    role: z
        .enum([USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.MANAGER, USER_ROLE.USER])
        .optional(),
    isActive: z.boolean().optional(),
});
export const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(1, 'Password is required'),
    turnstileToken: z.string().min(1, 'Verification required').optional(),
});
export const updateProfileSchema = z
    .object({
    name: z.string().min(1, 'Name is required').max(100).optional(),
    email: z.string().email('Invalid email address').optional(),
    currentPassword: z.string().min(1, 'Current password is required').optional(),
    newPassword: z.string().min(8, 'Password must be at least 8 characters').optional(),
})
    .refine(data => {
    // If newPassword is provided, currentPassword must also be provided
    if (data.newPassword && !data.currentPassword) {
        return false;
    }
    return true;
}, {
    message: 'Current password is required to set a new password',
    path: ['currentPassword'],
});
// ============================================
// Lead Schemas
// ============================================
export const createLeadSchema = z.object({
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    email: z.string().email('Invalid email').nullable().optional(),
    phone: z.string().max(30).nullable().optional(),
    company: z.string().max(100).nullable().optional(),
    title: z.string().max(100).nullable().optional(),
    source: z.enum([
        LEAD_SOURCE.WEBSITE,
        LEAD_SOURCE.REFERRAL,
        LEAD_SOURCE.COLD_CALL,
        LEAD_SOURCE.TRADE_SHOW,
        LEAD_SOURCE.PARTNER,
        LEAD_SOURCE.OTHER,
    ]),
    notes: z.string().max(5000).nullable().optional(),
});
export const updateLeadSchema = z.object({
    firstName: z.string().min(1).max(50).optional(),
    lastName: z.string().min(1).max(50).optional(),
    email: z.string().email().nullable().optional(),
    phone: z.string().max(30).nullable().optional(),
    company: z.string().max(100).nullable().optional(),
    title: z.string().max(100).nullable().optional(),
    status: z
        .enum([
        LEAD_STATUS.NEW,
        LEAD_STATUS.CONTACTED,
        LEAD_STATUS.QUALIFIED,
        LEAD_STATUS.UNQUALIFIED,
        LEAD_STATUS.CONVERTED,
    ])
        .optional(),
    source: z
        .enum([
        LEAD_SOURCE.WEBSITE,
        LEAD_SOURCE.REFERRAL,
        LEAD_SOURCE.COLD_CALL,
        LEAD_SOURCE.TRADE_SHOW,
        LEAD_SOURCE.PARTNER,
        LEAD_SOURCE.OTHER,
    ])
        .optional(),
    notes: z.string().max(5000).nullable().optional(),
});
export const leadListParamsSchema = listParamsSchema.extend({
    status: z
        .enum([
        LEAD_STATUS.NEW,
        LEAD_STATUS.CONTACTED,
        LEAD_STATUS.QUALIFIED,
        LEAD_STATUS.UNQUALIFIED,
        LEAD_STATUS.CONVERTED,
    ])
        .optional(),
    source: z
        .enum([
        LEAD_SOURCE.WEBSITE,
        LEAD_SOURCE.REFERRAL,
        LEAD_SOURCE.COLD_CALL,
        LEAD_SOURCE.TRADE_SHOW,
        LEAD_SOURCE.PARTNER,
        LEAD_SOURCE.OTHER,
    ])
        .optional(),
    ownerId: z.string().uuid().optional(),
});
export const convertLeadSchema = z
    .object({
    createNewAccount: z.boolean(),
    existingAccountId: z.string().uuid().optional(),
    accountName: z.string().min(1).max(100).optional(),
    createOpportunity: z.boolean().default(false),
    opportunityName: z.string().min(1).max(200).optional(),
    opportunityAmount: z.number().min(0).optional(),
    opportunityCloseDate: z.string().datetime().optional(),
})
    .refine(data => {
    if (data.createNewAccount && !data.accountName) {
        return false;
    }
    if (!data.createNewAccount && !data.existingAccountId) {
        return false;
    }
    return true;
}, {
    message: 'Either accountName (for new) or existingAccountId must be provided',
});
// ============================================
// Account Schemas
// ============================================
export const createAccountSchema = z.object({
    name: z.string().min(1, 'Account name is required').max(100),
    type: z
        .enum([
        ACCOUNT_TYPE.PROSPECT,
        ACCOUNT_TYPE.CUSTOMER,
        ACCOUNT_TYPE.PARTNER,
        ACCOUNT_TYPE.COMPETITOR,
    ])
        .default(ACCOUNT_TYPE.PROSPECT),
    industry: z
        .enum([
        INDUSTRY.HOTEL,
        INDUSTRY.RESORT,
        INDUSTRY.GUESTHOUSE,
        INDUSTRY.SAFARI_VESSEL,
        INDUSTRY.CRUISE,
        INDUSTRY.RESTAURANT,
        INDUSTRY.GOVERNMENT,
        INDUSTRY.HEALTHCARE,
        INDUSTRY.RETAIL,
        INDUSTRY.CORPORATE,
        INDUSTRY.DISTRIBUTOR,
        INDUSTRY.OTHER,
    ])
        .nullable()
        .optional(),
    website: z.string().url().nullable().optional(),
    phone: z.string().max(30).nullable().optional(),
    address: z.string().max(200).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    state: z.string().max(100).nullable().optional(),
    country: z.string().max(100).nullable().optional(),
    postalCode: z.string().max(20).nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
});
export const updateAccountSchema = createAccountSchema.partial();
export const accountListParamsSchema = listParamsSchema.extend({
    type: z
        .enum([
        ACCOUNT_TYPE.PROSPECT,
        ACCOUNT_TYPE.CUSTOMER,
        ACCOUNT_TYPE.PARTNER,
        ACCOUNT_TYPE.COMPETITOR,
    ])
        .optional(),
    industry: z
        .enum([
        INDUSTRY.HOTEL,
        INDUSTRY.RESORT,
        INDUSTRY.GUESTHOUSE,
        INDUSTRY.SAFARI_VESSEL,
        INDUSTRY.CRUISE,
        INDUSTRY.RESTAURANT,
        INDUSTRY.GOVERNMENT,
        INDUSTRY.HEALTHCARE,
        INDUSTRY.RETAIL,
        INDUSTRY.CORPORATE,
        INDUSTRY.DISTRIBUTOR,
        INDUSTRY.OTHER,
    ])
        .optional(),
    ownerId: z.string().uuid().optional(),
});
// ============================================
// Tenant Schemas
// ============================================
const tenantSlugSchema = z
    .string()
    .trim()
    .min(2, 'Slug is required')
    .max(100, 'Slug is too long')
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase and URL-safe');
const tenantNameSchema = z.string().trim().min(1, 'Tenant name is required').max(100);
const tenantSettingsSchema = z.record(z.unknown()).optional();
const booleanQuerySchema = z.preprocess(value => {
    if (value === 'true')
        return true;
    if (value === 'false')
        return false;
    return value;
}, z.boolean());
export const tenantIdParamSchema = z.object({
    id: z
        .string()
        .trim()
        .min(2, 'Tenant id is required')
        .max(100, 'Tenant id is too long')
        .regex(/^[a-z0-9_-]+$/i, 'Invalid tenant id format'),
});
export const createTenantSchema = z.object({
    name: tenantNameSchema,
    slug: tenantSlugSchema,
    settings: tenantSettingsSchema,
    isActive: z.boolean().optional(),
});
export const updateTenantSchema = z
    .object({
    name: tenantNameSchema.optional(),
    slug: tenantSlugSchema.optional(),
    settings: tenantSettingsSchema,
    isActive: z.boolean().optional(),
})
    .refine(data => Object.keys(data).length > 0, {
    message: 'At least one field is required',
});
export const tenantListParamsSchema = listParamsSchema.extend({
    isActive: booleanQuerySchema.optional(),
});
const tenantUserRoleSchema = z.enum([TENANT_USER_ROLE.MANAGER, TENANT_USER_ROLE.USER]);
export const tenantUserIdParamSchema = z.object({
    id: tenantIdParamSchema.shape.id,
    userId: idSchema,
});
export const userIdParamSchema = z.object({
    id: idSchema,
});
export const assignTenantUserSchema = z.object({
    userId: idSchema,
    role: tenantUserRoleSchema.default(TENANT_USER_ROLE.USER),
    isPrimary: z.boolean().optional(),
});
export const updateTenantUserRoleSchema = z.object({
    role: tenantUserRoleSchema,
});
export const tenantUsersListParamsSchema = z.object({
    role: tenantUserRoleSchema.optional(),
    isPrimary: booleanQuerySchema.optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
// ============================================
// Admin (v2) Schemas
// ============================================
export const adminDashboardQuerySchema = z
    .object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
})
    .refine(data => {
    if (!data.from || !data.to) {
        return true;
    }
    return new Date(data.from).getTime() <= new Date(data.to).getTime();
}, {
    message: '`from` must be before or equal to `to`',
    path: ['from'],
});
export const adminTenantLeadsQuerySchema = leadListParamsSchema.partial();
export const adminActivityQuerySchema = z
    .object({
    tenantId: tenantIdParamSchema.shape.id.optional(),
    entityType: z.enum(['lead', 'account', 'contact', 'opportunity', 'user', 'import']).optional(),
    action: z
        .enum([
        'create',
        'update',
        'delete',
        'convert',
        'lead_import',
        'account_import',
        'contact_import',
    ])
        .optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
})
    .refine(data => {
    if (!data.from || !data.to) {
        return true;
    }
    return new Date(data.from).getTime() <= new Date(data.to).getTime();
}, {
    message: '`from` must be before or equal to `to`',
    path: ['from'],
});
const onboardingStarterTemplateSchema = z.enum(['minimal', 'sales']);
export const onboardingInitialAssignmentSchema = z.object({
    userId: idSchema,
    role: tenantUserRoleSchema.default(TENANT_USER_ROLE.USER),
    isPrimary: z.boolean().optional(),
});
export const createTenantOnboardingSchema = z
    .object({
    tenant: createTenantSchema.pick({
        name: true,
        slug: true,
        settings: true,
    }),
    initialAssignments: z
        .array(onboardingInitialAssignmentSchema)
        .min(1, 'At least one initial assignment is required'),
    starterData: z
        .object({
        enabled: z.boolean().default(false),
        template: onboardingStarterTemplateSchema.optional(),
    })
        .default({ enabled: false }),
})
    .refine(data => {
    if (!data.starterData.enabled) {
        return true;
    }
    return !!data.starterData.template;
}, {
    message: 'Starter data template is required when starter data is enabled',
    path: ['starterData', 'template'],
})
    .refine(data => {
    const primaryAssignments = data.initialAssignments.filter(assignment => assignment.isPrimary);
    return primaryAssignments.length <= 1;
}, {
    message: 'Only one initial assignment can be primary',
    path: ['initialAssignments'],
});
export const onboardingStatusParamSchema = tenantIdParamSchema;
export const retryOnboardingSeedSchema = z.object({
    template: onboardingStarterTemplateSchema.default('minimal'),
});
export const onboardingSeedingStatusSchema = z.enum([
    'not_requested',
    'started',
    'completed',
    'failed',
]);
// ============================================
// Contact Schemas
// ============================================
export const createContactSchema = z.object({
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    email: z.string().email('Invalid email').nullable().optional(),
    phone: z.string().max(30).nullable().optional(),
    mobile: z.string().max(30).nullable().optional(),
    title: z.string().max(100).nullable().optional(),
    department: z.string().max(100).nullable().optional(),
    accountId: z.string().uuid('Invalid account ID'),
    isPrimary: z.boolean().default(false),
    notes: z.string().max(5000).nullable().optional(),
});
export const updateContactSchema = createContactSchema.partial().omit({ accountId: true });
export const contactListParamsSchema = listParamsSchema.extend({
    accountId: z.string().uuid().optional(),
    ownerId: z.string().uuid().optional(),
});
// ============================================
// Opportunity Schemas
// ============================================
export const createOpportunitySchema = z.object({
    name: z.string().min(1, 'Opportunity name is required').max(200),
    accountId: z.string().uuid('Invalid account ID'),
    contactId: z.string().uuid().nullable().optional(),
    stage: z
        .enum([
        OPPORTUNITY_STAGE.QUALIFICATION,
        OPPORTUNITY_STAGE.NEEDS_ANALYSIS,
        OPPORTUNITY_STAGE.PROPOSAL,
        OPPORTUNITY_STAGE.NEGOTIATION,
        OPPORTUNITY_STAGE.CLOSED_WON,
        OPPORTUNITY_STAGE.CLOSED_LOST,
    ])
        .default(OPPORTUNITY_STAGE.QUALIFICATION),
    amount: z.number().min(0, 'Amount must be positive').default(0),
    expectedCloseDate: z.string().datetime(),
    notes: z.string().max(5000).nullable().optional(),
});
export const updateOpportunitySchema = z.object({
    name: z.string().min(1).max(200).optional(),
    contactId: z.string().uuid().nullable().optional(),
    stage: z
        .enum([
        OPPORTUNITY_STAGE.QUALIFICATION,
        OPPORTUNITY_STAGE.NEEDS_ANALYSIS,
        OPPORTUNITY_STAGE.PROPOSAL,
        OPPORTUNITY_STAGE.NEGOTIATION,
        OPPORTUNITY_STAGE.CLOSED_WON,
        OPPORTUNITY_STAGE.CLOSED_LOST,
    ])
        .optional(),
    amount: z.number().min(0).optional(),
    expectedCloseDate: z.string().datetime().optional(),
    notes: z.string().max(5000).nullable().optional(),
});
export const opportunityListParamsSchema = listParamsSchema.extend({
    stage: z
        .enum([
        OPPORTUNITY_STAGE.QUALIFICATION,
        OPPORTUNITY_STAGE.NEEDS_ANALYSIS,
        OPPORTUNITY_STAGE.PROPOSAL,
        OPPORTUNITY_STAGE.NEGOTIATION,
        OPPORTUNITY_STAGE.CLOSED_WON,
        OPPORTUNITY_STAGE.CLOSED_LOST,
    ])
        .optional(),
    accountId: z.string().uuid().optional(),
    ownerId: z.string().uuid().optional(),
    minAmount: z.coerce.number().min(0).optional(),
    maxAmount: z.coerce.number().min(0).optional(),
});
// ============================================
// Line Item Schemas
// ============================================
export const createLineItemSchema = z.object({
    productName: z.string().min(1, 'Product name is required').max(200),
    quantity: z.number().int().positive('Quantity must be positive'),
    unitPrice: z.number().min(0, 'Unit price must be positive'),
    notes: z.string().max(1000).nullable().optional(),
});
export const updateLineItemSchema = createLineItemSchema.partial();
// ============================================
// Batch Import Schemas
// ============================================
/**
 * Single lead record for import
 * Uses relaxed validation for batch processing - allows nulls for optional fields
 */
export const importLeadSchema = z.object({
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    email: z.string().email('Invalid email').nullable().optional(),
    phone: z.string().max(30).nullable().optional(),
    company: z.string().max(100).nullable().optional(),
    title: z.string().max(100).nullable().optional(),
    source: z
        .enum([
        LEAD_SOURCE.WEBSITE,
        LEAD_SOURCE.REFERRAL,
        LEAD_SOURCE.COLD_CALL,
        LEAD_SOURCE.TRADE_SHOW,
        LEAD_SOURCE.PARTNER,
        LEAD_SOURCE.OTHER,
    ])
        .default(LEAD_SOURCE.OTHER),
    notes: z.string().max(5000).nullable().optional(),
});
/**
 * Batch import request for leads
 */
export const importLeadsSchema = z.object({
    leads: z
        .array(importLeadSchema)
        .min(1, 'At least one lead is required')
        .max(500, 'Maximum 500 leads per import'),
    skipDuplicates: z.boolean().default(true),
});
/**
 * Single account record for import
 */
export const importAccountSchema = z.object({
    name: z.string().min(1, 'Account name is required').max(100),
    type: z
        .enum([
        ACCOUNT_TYPE.PROSPECT,
        ACCOUNT_TYPE.CUSTOMER,
        ACCOUNT_TYPE.PARTNER,
        ACCOUNT_TYPE.COMPETITOR,
    ])
        .default(ACCOUNT_TYPE.PROSPECT),
    industry: z
        .enum([
        INDUSTRY.HOTEL,
        INDUSTRY.RESORT,
        INDUSTRY.GUESTHOUSE,
        INDUSTRY.SAFARI_VESSEL,
        INDUSTRY.CRUISE,
        INDUSTRY.RESTAURANT,
        INDUSTRY.GOVERNMENT,
        INDUSTRY.HEALTHCARE,
        INDUSTRY.RETAIL,
        INDUSTRY.CORPORATE,
        INDUSTRY.DISTRIBUTOR,
        INDUSTRY.OTHER,
    ])
        .nullable()
        .optional(),
    website: z.string().url().nullable().optional(),
    phone: z.string().max(30).nullable().optional(),
    address: z.string().max(200).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    state: z.string().max(100).nullable().optional(),
    country: z.string().max(100).nullable().optional(),
    postalCode: z.string().max(20).nullable().optional(),
    notes: z.string().max(5000).nullable().optional(),
});
/**
 * Batch import request for accounts
 */
export const importAccountsSchema = z.object({
    accounts: z
        .array(importAccountSchema)
        .min(1, 'At least one account is required')
        .max(500, 'Maximum 500 accounts per import'),
    skipDuplicates: z.boolean().default(true),
});
/**
 * Single contact record for import
 * accountId or accountName required to link contact
 */
export const importContactSchema = z.object({
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    email: z.string().email('Invalid email').nullable().optional(),
    phone: z.string().max(30).nullable().optional(),
    mobile: z.string().max(30).nullable().optional(),
    title: z.string().max(100).nullable().optional(),
    department: z.string().max(100).nullable().optional(),
    accountId: z.string().uuid('Invalid account ID').optional(),
    accountName: z.string().max(100).optional(), // Alternative to accountId for lookups
    isPrimary: z.boolean().default(false),
    notes: z.string().max(5000).nullable().optional(),
});
/**
 * Batch import request for contacts
 */
export const importContactsSchema = z.object({
    contacts: z
        .array(importContactSchema)
        .min(1, 'At least one contact is required')
        .max(500, 'Maximum 500 contacts per import'),
    skipDuplicates: z.boolean().default(true),
    createMissingAccounts: z.boolean().default(false), // If true, create accounts for unmatched accountName
});
/**
 * Import result for a single record
 */
export const importResultSchema = z.object({
    row: z.number(),
    success: z.boolean(),
    id: z.string().optional(),
    error: z.string().optional(),
    data: z.record(z.unknown()).optional(), // Original input data for reference
});
//# sourceMappingURL=schemas.js.map